export enum UserRole {
  USER = "USER",
  TEAM_ADMIN = "TEAM_ADMIN",
  TEAM_USER = "TEAM_USER",
  ADMIN = "ADMIN",
}

export type Address = {
  street: string;
  city: string;
  state: string;
  zipCode: string;
};

export type User = {
  id: string;
  nameFirst: string;
  nameLast: string;
  email: string;
  role: UserRole;
  isAdmin?: boolean | "true" | "false";
  mlsApproved: boolean;
  // MB-1399: developer testing mode — publishing short-circuits at the
  // Ayrshare boundary; admin-set only.
  simulatePosting?: boolean;
  // MB-1491: with Developer Testing Mode also on, content generation
  // short-circuits to placeholder content (zero OpenAI calls); admin-set
  // only, inert unless simulatePosting is also true.
  simulateContentGeneration?: boolean;
  onboarded: boolean;
  licenseNumber: string;
  companyAddress: Address;
  companyName: string;
  phoneNumber: string;
  broker: string;
  brokerLicense: string;
  brokerEmail: string;
  mls?: string | MLS;
  limits?: Limits;
  otherMLS: string;
  timeZone: string;
  verified: boolean;
  status: string;
  accountId?: string;
  plan?: Plan;
  account: Account;
  adminRole?: 'CREATOR' | 'ADMIN' | null;
};

export interface Account {
  activeSubscription: Subscription | null;
}

export interface Subscription {
  plan: Plan;
  startDate: Date;
  endDate: Date;
  status: SubscriptionStatus;
  expiresAt: Date;
}

export enum SubscriptionStatus {
  Active = "ACTIVE",
  Inactive = "INACTIVE",
  PendingAgreement = "PENDING",
  Paused = "PAUSED",
}

export interface EditUserOverlayProps {
  editingUser: Partial<User> | null;
  setEditingUser: (user: User | null) => void;
  teamAdmins: User[];
  onSave: () => void;
  isAdd: boolean;
}

export enum planFrequency {
  Monthly = "Monthly",
}

export interface Limits {
  postWarn: number;
  postLimit: number;
  activeListingLimit: number;
  listingWarn: number;
  listingLimit: number;
  imageTagsWarn: number;
  imageTagsLimit: number;
  imageVideosWarn: number;
  imageVideosLimit: number;
  userLimit: number;
  storageWarn?: number;
  storageLimit?: number;
  monthlyTransferWarn?: number;
  monthlyTransferLimit?: number;
  rentcastWarn: number;
  rentcastLimit: number;
  zapcapSecondsLimit?: number;
  // MB-1377: 0 = no autopilot; >0 = listings autopilot may cover (AI Pro: 12)
  autopilotListingLimit?: number;
  // Niche Finder monthly AI allowance in micro-dollars (0 = none; AI Pro: 2_000_000 = $2).
  interactiveAiLimitMicros?: number;
}

export interface Plan {
  id?: string;
  name: string;
  description: string;
  userNotes: string;
  price: number | string;
  frequency: planFrequency;
  public: boolean;
  limits: Limits;
}

export const defaultLimits: Limits = {
  postWarn: 1,
  postLimit: 1,
  activeListingLimit: 1,
  listingWarn: 1,
  listingLimit: 1,
  imageTagsWarn: 1,
  imageTagsLimit: 1,
  imageVideosWarn: 1,
  imageVideosLimit: 1,
  userLimit: 1,
  rentcastWarn: 1,
  rentcastLimit: 1,
  monthlyTransferLimit: 1,
  monthlyTransferWarn: 1,
  storageLimit: 1,
  storageWarn: 1,
};

export interface PlanFormProps {
  plan: Plan;
  setEditingPlan: (plan: Plan | null) => void;
  onClose: () => void;
}

export interface DashboardData {
  totalUsers: number;
  lastMonthUsers: number;
  percent: string;
}

export interface MLS {
  _id: string;
  name: string;
  description: string;
  link: string;
}

export interface MLSUsage {
  _id: string;
  userId?: string;
  sync: boolean;
  type: MLSUsageType;
  timestamp: number;
  createdAt: string;
}

export enum MLSUsageType {
  Data = "Data",
  Media = "Media",
}

export interface MLSUsageTimeData {
  count: number;
  createdAt?: number;
  timestamp?: number;
}

export interface MLSUsagePeriod {
  dataUsage: MLSUsageTimeData[];
  mediaUsage: MLSUsageTimeData[];
}

export interface HourlyMLSUsage {
  hourly: MLSUsagePeriod;
  daily: MLSUsagePeriod;
  monthly: MLSUsagePeriod;
  yearly: MLSUsagePeriod;
}

export interface MLSUsageTableData {
  date: string;
  timestamp: number;
  dataCount: number;
  mediaCount: number;
  totalCount: number;
}

export interface RentcastUsage {
  _id: string;
  userId: string;
  timestamp: number;
  createdAt: string;
  updatedAt: string;
}

export interface MonthlyRentcastUsage {
  timestamp: number;
  count: number;
}

export interface UserInfo {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface GroupedRentcastUser {
  user: UserInfo;
  usage: RentcastUsage[];
  totalUsageCount: number;
}

export interface PaginatedRentcastResponse {
  data: GroupedRentcastUser[];
  pagination: {
    page: number;
    limit: number;
    totalUsers: number;
    totalPages: number;
  };
}

export interface PaginatedUserUsageResponse {
  data: RentcastUsage[];
  total: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface OpenAIUsage {
  _id: string;
  userId: string;
  model: OpenAIModel;
  inputTokens: number;
  outputTokens: number;
  cost: number;
  createdAt: string;
  updatedAt: string;
  timestamp: number;
}

export enum OpenAIModel {
  GPT4o = "GPT4o",
  GPT4 = "GPT4",
  GPT4oVision = "GPT4oVision",
  GPT4oBatch = "GPT4oBatch",
}

export interface MonthlyOpenAIUsage {
  _id: string;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCost: number;
  userId: string;
  year: number;
  month: number;
  model: OpenAIModel;
}

export interface OpenAIMonthlyData {
  year: number;
  month: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCost: number;
}

export interface OpenAIUserData {
  totalCost: number;
  userId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  // MB-1403: user doc no longer exists — email is the write-time snapshot.
  deleted?: boolean;
  monthlyData: OpenAIMonthlyData[];
}

// MB-1403: spend grouped by what the call was for.
export interface OpenAIActivityData {
  totalCost: number;
  activity: string;
  monthlyData: OpenAIMonthlyData[];
}

// MB-1403: environment split from OpenAI's Costs API (project keys).
export interface OpenAIEnvironmentSpend {
  configured: boolean;
  days: number;
  projects: { projectId: string; name: string; amountUsd: number }[];
  totalUsd: number;
  // MB-1403: configured, but the billing call failed — distinct from
  // "not configured yet" so a broken admin key doesn't read as a pending rollout.
  error?: string;
}

export interface OpenAISummary {
  totalCost: number;
  totalInputTokens: number;
  totalOutputTokens: number;
}

export interface OpenAIUsageResponse {
  summary: OpenAISummary;
  users: OpenAIUserData[];
  activities?: OpenAIActivityData[];
}

export interface Maintenance {
  isOnline: boolean;
  reason: string;
}

export const defaultMaintenance: Maintenance = {
  isOnline: true,
  reason: "",
};

export interface MaintenanceFormProps {
  maintenance: Maintenance;
  setMaintenance: React.Dispatch<React.SetStateAction<Maintenance>>;
  originalMaintenance: Maintenance;
  type: "general" | "ai";
  onSave: () => void;
  loading: boolean;
}

export interface LimitsTabProps {
  limits: Limits | undefined;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}
